import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, Smartphone } from 'lucide-react';
import QRCode from 'qrcode';
import { useNavigate } from 'react-router-dom';
import type { UserSummary } from '@shared/types';
import { useAuth } from '@/app/auth-context';
import { Button, Input, Label } from '@/components/ui';
import { apiFetch, getErrorMessage } from '@/lib/api';
import { routes } from '@/lib/routes';
import { usePageMeta } from '@/lib/seo';

interface AdminAuthStatus {
  configured: boolean;
  bootstrapRequired: boolean;
}

interface EnrollmentDetails {
  enrollmentToken: string;
  manualKey: string;
  provisioningUri: string;
  expiresAt: string;
}

function AuthenticatorCodeInput(props: {
  value: string;
  onChange: (value: string) => void;
  id: string;
}) {
  return (
    <Input
      id={props.id}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={6}
      placeholder="000000"
      value={props.value}
      onChange={(event) => props.onChange(event.target.value.replace(/\D/gu, '').slice(0, 6))}
      className="text-center font-mono text-xl tracking-[0.35em]"
    />
  );
}

export function AdminLoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [bootstrapCode, setBootstrapCode] = useState('');
  const [authenticatorCode, setAuthenticatorCode] = useState('');
  const [enrollment, setEnrollment] = useState<EnrollmentDetails | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const status = useQuery({
    queryKey: ['admin-auth-status'],
    queryFn: () => apiFetch<AdminAuthStatus>('/api/admin/auth/status'),
    retry: false,
  });

  const finishSignIn = async () => {
    await refresh();
    void navigate(routes.admin, { replace: true });
  };

  const beginEnrollment = useMutation({
    mutationFn: () =>
      apiFetch<EnrollmentDetails>('/api/admin/auth/totp/enroll/begin', {
        method: 'POST',
        body: JSON.stringify({ email, fullName, bootstrapCode }),
      }),
    onSuccess: (details) => {
      setEnrollment(details);
      setAuthenticatorCode('');
      setBootstrapCode('');
    },
  });

  const completeEnrollment = useMutation({
    mutationFn: () => {
      if (!enrollment) throw new Error('Authenticator setup is missing.');
      return apiFetch<{ user: UserSummary }>('/api/admin/auth/totp/enroll/complete', {
        method: 'POST',
        body: JSON.stringify({
          enrollmentToken: enrollment.enrollmentToken,
          code: authenticatorCode,
        }),
      });
    },
    onSuccess: finishSignIn,
  });

  const login = useMutation({
    mutationFn: () =>
      apiFetch<{ user: UserSummary }>('/api/admin/auth/totp/login', {
        method: 'POST',
        body: JSON.stringify({ email, code: authenticatorCode }),
      }),
    onSuccess: finishSignIn,
  });

  useEffect(() => {
    if (!enrollment) return;
    let cancelled = false;
    void QRCode.toDataURL(enrollment.provisioningUri, {
      width: 256,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((value) => {
        if (!cancelled) setQrDataUrl(value);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enrollment]);

  const displayManualKey = useMemo(
    () => enrollment?.manualKey.match(/.{1,4}/gu)?.join(' ') ?? '',
    [enrollment],
  );
  const error =
    beginEnrollment.error ?? completeEnrollment.error ?? login.error ?? status.error ?? null;

  usePageMeta({
    title: 'Administrator sign in',
    description: 'Secure CareersLaunching administrator access.',
    noIndex: true,
  });

  return (
    <div className="auth-card max-w-lg">
      <span className="auth-card__eyebrow">
        <ShieldCheck size={17} /> Admin portal
      </span>
      <h1>Secure administrator access</h1>
      <p>Use the six-digit code from Google Authenticator on your phone.</p>

      {status.isPending ? <p>Checking administrator setup…</p> : null}
      {status.data && !status.data.configured ? (
        <p className="form-error" role="alert">
          Mobile authenticator sign-in has not been configured on the server yet.
        </p>
      ) : null}

      {status.data?.bootstrapRequired && !enrollment ? (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            beginEnrollment.mutate();
          }}
        >
          <div className="field-stack">
            <Label htmlFor="admin-name">Your name</Label>
            <Input
              id="admin-name"
              autoComplete="name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
            />
          </div>
          <div className="field-stack">
            <Label htmlFor="admin-email">Administrator email</Label>
            <Input
              id="admin-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="field-stack">
            <Label htmlFor="bootstrap-code">One-time setup code</Label>
            <Input
              id="bootstrap-code"
              type="password"
              autoComplete="one-time-code"
              value={bootstrapCode}
              onChange={(event) => setBootstrapCode(event.target.value)}
            />
            <p className="field-help">This code stops working after the first admin is linked.</p>
          </div>
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={
              beginEnrollment.isPending ||
              !status.data.configured ||
              fullName.trim().length < 2 ||
              !email.includes('@') ||
              bootstrapCode.trim().length < 16
            }
          >
            <Smartphone size={18} />
            {beginEnrollment.isPending ? 'Preparing authenticator…' : 'Set up mobile app'}
          </Button>
        </form>
      ) : null}

      {status.data?.bootstrapRequired && enrollment ? (
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center">
            <p className="mb-3 font-semibold text-slate-900">
              Scan this QR code in Google Authenticator
            </p>
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="CareersLaunching authenticator setup QR code"
                className="mx-auto size-64 max-w-full rounded-xl"
              />
            ) : (
              <div className="mx-auto size-64 max-w-full animate-pulse rounded-xl bg-slate-100" />
            )}
          </div>
          <div className="field-stack">
            <Label>Can’t scan it? Enter this setup key</Label>
            <div className="rounded-xl bg-slate-100 p-3 text-center font-mono text-sm font-semibold tracking-wider break-all text-slate-900">
              {displayManualKey}
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                void navigator.clipboard
                  .writeText(enrollment.manualKey)
                  .then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2_000);
                  })
                  .catch(() => setCopied(false));
              }}
            >
              {copied ? 'Copied' : 'Copy setup key'}
            </Button>
          </div>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              completeEnrollment.mutate();
            }}
          >
            <div className="field-stack">
              <Label htmlFor="enrollment-authenticator-code">Six-digit code from the app</Label>
              <AuthenticatorCodeInput
                id="enrollment-authenticator-code"
                value={authenticatorCode}
                onChange={setAuthenticatorCode}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={completeEnrollment.isPending || authenticatorCode.length !== 6}
            >
              <KeyRound size={18} />
              {completeEnrollment.isPending ? 'Verifying…' : 'Verify and open admin portal'}
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                setEnrollment(null);
                setQrDataUrl(null);
                setAuthenticatorCode('');
              }}
            >
              Start again
            </Button>
          </form>
        </div>
      ) : null}

      {status.data && !status.data.bootstrapRequired ? (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            login.mutate();
          }}
        >
          <div className="field-stack">
            <Label htmlFor="admin-login-email">Administrator email</Label>
            <Input
              id="admin-login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="field-stack">
            <Label htmlFor="login-authenticator-code">Google Authenticator code</Label>
            <AuthenticatorCodeInput
              id="login-authenticator-code"
              value={authenticatorCode}
              onChange={setAuthenticatorCode}
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={
              login.isPending ||
              !status.data.configured ||
              !email.includes('@') ||
              authenticatorCode.length !== 6
            }
          >
            <KeyRound size={18} />
            {login.isPending ? 'Signing in…' : 'Open admin portal'}
          </Button>
        </form>
      ) : null}

      {error ? (
        <p className="form-error" role="alert">
          {getErrorMessage(error, 'Administrator sign-in could not be completed.')}
        </p>
      ) : null}
    </div>
  );
}

export default AdminLoginPage;
